// The Oneiros Journal — editorial blog content for Oneiros Sports Club.
// All imagery is official club material; claims mirror the website (cluboneiros.com) and club leaflets. Nothing invented.

const img = (name) => `/oneiros/${name}`;

export const BLOG_POSTS = [
  {
    slug: "why-a-sports-club-beats-a-regular-gym",
    title: "Why a Sports Club Beats a Regular Gym",
    category: "Training",
    date: "September 12, 2026",
    readTime: "4 min read",
    excerpt:
      "A gym gives you equipment. A sports club gives you a reason to show up — courts, pools, coaches and a community that makes training the best part of your day.",
    image: img("kat-gym.jpg"),
    content: [
      { p: "Most people don't quit the gym because they're lazy. They quit because it gets boring — the same four walls, the same treadmill, the same playlist. A sports club solves that problem at the root: your week is built around sports you actually enjoy." },
      { h: "Variety is the real motivator" },
      { p: "At Oneiros Katargam and Adajan, a single membership opens a semi-Olympic swimming pool, badminton courts, lawn tennis, pickleball, karate, yoga and a European-style fitness studio. When Monday is strength day, Wednesday is swim day and Saturday is pickleball with your kids, you never train out of obligation." },
      { h: "Coaching compounds" },
      { p: "Equipment doesn't build athletes — coaching does. From the Kids Club game-identification programme to performance training at Asia's Strongest Gym, every age group trains under expert eyes. Technique first, intensity second: that's how you stay injury-free for decades." },
      { h: "The family effect" },
      { p: "The hardest part of fitness is protecting the time for it. When the whole family trains under one roof — one membership, one destination — workout time stops competing with family time. It becomes family time." },
      { p: "That's the idea behind everything we built at Oneiros: more than a gym — a complete sports, fitness and wellness ecosystem for the whole family." },
    ],
  },
  {
    slug: "blue-membership-explained",
    title: "The Blue Membership, Explained",
    category: "Membership",
    date: "September 16, 2026",
    readTime: "3 min read",
    excerpt:
      "One family of four, one home club, years of fitness together. Here's exactly how the Blue Membership works at Katargam, Adajan and Varachha.",
    image: img("blue-card.jpg"),
    content: [
      { p: "The Blue Membership is built around one simple promise: your club, your family, your years. Instead of four separate gym subscriptions, your entire family of four shares one membership at the Oneiros club you call home." },
      { h: "Pick your home club, pick your years" },
      { p: "Blue tenure depends on where your home club is: 12 years at Katargam, 15 years at Adajan and 25 years at Varachha. Each membership covers full access to that club — core sports, fitness facilities, group classes, recovery and wellness zones, plus special member events." },
      { h: "Prefer every club? That's Gold." },
      { p: "Gold opens all three Oneiros doors in Surat, with priority bookings, guest privileges and exclusive lounges. If your week swings between Katargam's performance floors and Adajan's leisure side, Gold is your pass." },
      { h: "The Blue Moon window" },
      { p: "For a limited pre-sale window, our first 500 members at Varachha get NO AMC for life — the Blue Moon Offer. Once in a blue moon, truly. The Blue Membership page has its own enquiry form if you'd like our team to walk you through current plans." },
    ],
  },
  {
    slug: "prakash-padukone-academy-surat",
    title: "Surat's First-Ever Prakash Padukone International Badminton Academy",
    category: "Badminton",
    date: "September 5, 2026",
    readTime: "3 min read",
    excerpt:
      "A new benchmark in professional badminton training has arrived in Surat. Here's what the Prakash Padukone Academy at Oneiros means for the city's players.",
    image: img("kat-badminton.jpg"),
    content: [
      { p: "Prakash Padukone changed Indian badminton — an All England champion who proved world-class players could be built in India. His academy model has produced generations of talent, and Surat now hosts its first-ever branch inside Oneiros." },
      { h: "What 'international standard' actually means" },
      { p: "Court quality, ceiling height, lighting, flooring grip, shuttle speed — every variable that shapes a player is engineered to the academy's specifications. It's the difference between playing badminton and training for it." },
      { h: "Discipline, technique, progression" },
      { p: "The coaching philosophy is the one that built champions: nail the technique, respect the discipline, progress through structured stages. Beginners learn footwork before smash power; competitive players sharpen match temperament." },
      { p: "Both Oneiros Katargam and Oneiros Adajan host the academy. Book a court or ask our concierge about coaching batches — the fastest way in is the enquiry form." },
    ],
  },
  {
    slug: "swimming-for-every-age",
    title: "Swimming: The Full-Body Sport Every Age Can Master",
    category: "Swimming",
    date: "August 28, 2026",
    readTime: "4 min read",
    excerpt:
      "Low impact, full-body, endlessly scalable — swimming might be the most family-friendly sport in Surat. And it runs all year at Oneiros.",
    image: img("kat-pool.jpg"),
    content: [
      { p: "Ask any physiotherapist and they'll tell you: if you could design one exercise for life, it would look like swimming. It builds the heart and lungs, tones the whole body, and spares the joints while doing it." },
      { h: "Surat's first indoor semi-Olympic pool" },
      { p: "Oneiros Katargam houses Surat's first and only indoor semi-Olympic swimming pool — temperature-controlled and weather-independent, so monsoon and summer never touch your training plan. Consistency is the entire secret of swimming, and an indoor pool makes consistency easy." },
      { h: "Every age, same water" },
      { p: "Toddlers build water confidence, schoolkids train strokes and stamina, parents swim laps at dawn, and grandparents keep their mobility moving with gentle aqua sessions. Few sports let a family share a workout this seamlessly." },
      { h: "Getting started" },
      { p: "Start with two sessions a week. Short, regular swims beat occasional heroic ones. Our coaches structure stroke correction and endurance in stages — just bring your kit and the willingness to get your hair wet." },
    ],
  },
  {
    slug: "the-5-lakh-kgs-revolution",
    title: "The 5 Lakh Kgs Revolution: One City, One Goal",
    category: "Community",
    date: "August 20, 2026",
    readTime: "3 min read",
    excerpt:
      "5,00,000 kilos. Half a million kilos of collective transformation. That's the mission Oneiros has set for Surat — and every member's journey counts toward it.",
    image: img("campaign-5l.jpg"),
    content: [
      { p: "Some goals are personal. This one is civic. Oneiros was founded in 2019 with a mission: help Surat collectively lose 5,00,000 kilos — 5L Kgs. One City. One Goal." },
      { h: "Why a collective number?" },
      { p: "Because transformation is contagious. When your neighbour drops a size and your colleague starts finishing workouts, fitness stops being intimidating and starts being normal. A city's health is the sum of thousands of small personal wins." },
      { h: "Expert training, smart nutrition, community support" },
      { p: "The mission stands on three pillars: expert coaching that keeps training safe and progressive, nutrition guidance that works with real Indian kitchens, and a community that celebrates every kilo — because every kilo is a life changed." },
      { p: "Half a million kilos. Zero excuses. Be part of the movement — start with your own Day One." },
    ],
  },
  {
    slug: "kids-club-game-identification",
    title: "Kids Club: How Game Identification Works",
    category: "Kids",
    date: "August 12, 2026",
    readTime: "4 min read",
    excerpt:
      "Six days, six activities, six expert coaches. Here's how we help children find the sport that fits them — without trial, error and forced choices.",
    image: img("kat-kids-club.jpg"),
    content: [
      { p: "Most parents guess at their child's sport. The kid tries one activity for a term, loses interest, and everyone concludes 'sports aren't for them'. The truth is almost every child loves the right sport — most never meet it." },
      { h: "The six-day rotation" },
      { p: "The Oneiros Kids Club runs a structured game-identification programme: six days, six activities, six expert coaches. Children rotate through Skating, Karate, Swimming, Dance, Yoga and CrossFit — each taught with age-appropriate, play-first methods." },
      { h: "What coaches actually watch" },
      { p: "Not performance — instinct. Which activity makes the child lean in. Where coordination looks natural. When effort stops needing reminders. Those signals, read by trained coaches, point to the sport a child will actually stick with." },
      { h: "Confidence first, competition later" },
      { p: "The goal of game identification isn't producing champions — it's producing kids who love being active. Champions are a happy side effect when the foundation is joy, confidence and coordination." },
    ],
  },
  {
    slug: "recovery-is-training-too",
    title: "Recovery Is Training Too",
    category: "Recovery",
    date: "August 5, 2026",
    readTime: "3 min read",
    excerpt:
      "The session doesn't end when you rack the barbell. Steam, sauna, jacuzzi and ice showers — recovery is where your body collects its gains.",
    image: img("fac-recovery.jpg"),
    content: [
      { p: "Training doesn't make you stronger. It makes you damaged — and recovery rebuilds you stronger. Skip the second half and you're only doing half the workout, no matter how perfect your sessions are." },
      { h: "Heat, cold and contrast" },
      { p: "Oneiros recovery zones pair steam and sauna with ice showers and jacuzzi. Heat relaxes tissue and improves circulation; cold tames inflammation and wakes up the nervous system. Alternating them — contrast recovery — feels punishing and works beautifully." },
      { h: "Recovery is a skill" },
      { p: "Treat recovery like training: scheduled, not accidental. Ten minutes of stillness after a session beats an hour of doom-scrolling on the couch. Pair your recovery zone visits with real sleep and hydration and your progress curve changes shape." },
      { p: "Your Blue and Gold memberships include recovery and wellness access — use it. The members who recover best are always the ones who progress fastest." },
    ],
  },
  {
    slug: "group-classes-that-stick",
    title: "Group Classes That Actually Stick",
    category: "Fitness",
    date: "July 29, 2026",
    readTime: "3 min read",
    excerpt:
      "Motivation fades — structure doesn't. Why expert-led group classes keep members showing up long after January resolutions die.",
    image: img("fac-fitness-studio.jpg"),
    content: [
      { p: "The fitness industry has a secret: nobody is disciplined all the time. The people who train for decades aren't the most motivated — they're the most structured. Group classes are structure you can join with one tap." },
      { h: "The energy of the floor" },
      { p: "There's a reason European-style fitness studios are designed around shared energy. A coach's cue, a neighbour's pace, music that carries the last thirty seconds — solo sessions rarely replicate that pull." },
      { h: "Programming beats improvisation" },
      { p: "Every Oneiros group class is a programmed session: warm-up, work, progression. You don't decide what to do when you're tired — the plan decides. That's how beginners avoid injury and regulars avoid plateaus." },
      { p: "Blue and Gold memberships include expert-led group classes for all four family members. Try a week of them — structure is contagious." },
    ],
  },
  {
    slug: "eat-to-train",
    title: "Eat to Train: Fueling Workouts in a Real Indian Kitchen",
    category: "Nutrition",
    date: "July 18, 2026",
    readTime: "4 min read",
    excerpt:
      "Dal, roti, curd and eggs beat any imported supplement plan. How to fuel training with the food already in your kitchen.",
    image: img("fac-cafe.jpg"),
    content: [
      { p: "The best training diet isn't exotic — it's your grandmother's cooking, portioned with intention. Protein at every meal, carbs around your workouts, and vegetables that show up on the plate like they own it." },
      { h: "Protein: the quiet king" },
      { p: "Dal, paneer, curd, eggs, chicken, fish — Indian kitchens are full of protein if you distribute it across the day. Most families front-load carbs at dinner and starve protein at breakfast; flipping that one habit changes bodies." },
      { h: "Fuel the session, not the couch" },
      { p: "Eat something light and carb-friendly sixty to ninety minutes before training. Refill with protein within a couple of hours after. The post-workout samosa isn't forbidden — it's just not fuel, it's a treat. Treats come after fuel." },
      { h: "And after the workout?" },
      { p: "The Oneiros cafe exists exactly for this — refuel the right way before you drive home. Ask our team for member favourites; the post-swim crowd already knows the menu." },
    ],
  },
  {
    slug: "racquet-sports-101",
    title: "Racquet Sports 101: Badminton, Tennis and Pickleball",
    category: "Sports",
    date: "July 8, 2026",
    readTime: "4 min read",
    excerpt:
      "Three racquets, three personalities. Which one is your sport — the sprint of badminton, the chess of tennis, or the social spark of pickleball?",
    image: img("adajan-lawn-tennis.jpg"),
    content: [
      { p: "Racquet sports are the best-kept secret of adult fitness: they're interval training disguised as play. Short bursts, quick feet, constant decisions — an hour flies by while your heart gets a masterclass." },
      { h: "Badminton: the sprinter's game" },
      { p: "Fastest racquet sport on earth. Tiny court, explosive rallies, reflexes that sharpen week by week. With the Prakash Padukone Academy at Oneiros, beginners get structured coaching and regulars get world-class courts." },
      { h: "Tennis: the strategist's game" },
      { p: "Longer points, bigger court, more chess. Tennis rewards patience and pattern recognition as much as power — and a lawn tennis session under the Surat sun builds an athlete's engine." },
      { h: "Pickleball: the social one" },
      { p: "Half the court, a third of the learning curve, all of the fun. Pickleball is why so many families at Oneiros finish their workouts with a court booked — grandparents can play it with grandchildren, and do." },
      { p: "Try all three across your first month. Your body will tell you which sport it was missing." },
    ],
  },
  {
    slug: "one-membership-whole-family",
    title: "One Membership, a Whole Family's Health",
    category: "Membership",
    date: "June 30, 2026",
    readTime: "3 min read",
    excerpt:
      "When four people train under one roof, fitness stops being a schedule conflict and becomes a family habit. The case for club-based family membership.",
    image: img("blue-family-four.jpg"),
    content: [
      { p: "Every family knows the pattern: dad's gym at 6, mom's yoga app at 7, kids dropped at activity classes on Saturday. Four schedules, four costs, four chances to quit. A family club membership collapses all four into one destination." },
      { h: "Same roof, different goals" },
      { p: "At Oneiros, dad can chase strength numbers, mom can own the fitness studio, the teenager can train badminton at the Padukone Academy and the little one can explore the Kids Club — simultaneously, in one place, under one membership." },
      { h: "The math families actually care about" },
      { p: "Blue Membership covers a family of four at one home club — 12 years at Katargam, 15 at Adajan, 25 at Varachha. Gold unlocks all three clubs with priority bookings and guest privileges. Our team shares current plans on enquiry — no prices hidden in fine print, no pressure." },
      { p: "Families that train together don't just get fitter. They build the rarest thing in modern life: a shared routine." },
    ],
  },
  {
    slug: "varachha-presale-guide",
    title: "Varachha Is Opening: What Pre-Sale Really Means",
    category: "Pre-Sale",
    date: "September 20, 2026",
    readTime: "3 min read",
    excerpt:
      "Oneiros Varachha's pre-sale is live — and the Blue Moon Offer for the first 500 members is the best membership window Oneiros has ever opened.",
    image: img("varachha-outer.png"),
    content: [
      { p: "A pre-sale isn't a discount gimmick — it's the moment a club rewards the people who believed first. Oneiros Varachha's doors are opening, and the first 500 members step in through the Blue Moon Offer: NO AMC for life." },
      { h: "What the Blue One gets you" },
      { p: "Blue Membership at Varachha is a 25-year home-club tenure for your family of four — the longest tenure in the Oneiros family. Premium gym, sky deck run, swimming pool, fitness studio, cricket arena, lawn tennis, squash, kids club, meditation sphere and the full wellness suite." },
      { h: "Why the first 500 matter" },
      { p: "Founding members shape a club's culture. The first community sets the energy on the floors, the faces in the classes, the families at the events. Once in a blue moon — this window closes when it closes." },
      { p: "Pre-sale enquiries are open now. Hit the Blue Moon Offer CTA on the Varachha page or the home page banner, and the Blue One — our Varachha mascot — will make sure your family's details reach the team first." },
    ],
  },
];

export const getPost = (slug) => BLOG_POSTS.find((b) => b.slug === slug);
